import { type LanguageSetting } from "./language";

export const BOARD_WIDTH = 6;
export const BOARD_HEIGHT = 10;
export const CELL_BASE_SIZE = 32;
export const MAX_LEVEL = 30;
export const MIN_TYPE_COUNT = 4;
export const MAX_TYPE_COUNT = 6;

export type EmptyCell = 0;
export type PieceType = 1 | 2 | 3 | 4 | 5 | 6;
export type Cell = EmptyCell | PieceType;
export type Board = Cell[][];
export type PairOrientation = 0 | 1 | 2 | 3;

export type SceneState =
  | "boot"
  | "loadingAssets"
  | "opening"
  | "playing"
  | "dropping"
  | "paused"
  | "resolvingErase"
  | "applyingGravity"
  | "gameOverSequence"
  | "gameOver"
  | "nameEntry"
  | "ranking"
  | "help"
  | "settings";

export interface Position {
  x: number;
  y: number;
}

export interface PairCell extends Position {
  type: PieceType;
}

export interface FallingPair {
  x: number;
  y: number;
  upperType: PieceType;
  lowerType: PieceType;
  orientation?: PairOrientation;
}

export interface PairAnimation {
  from: FallingPair;
  to: FallingPair;
  elapsed: number;
  duration: number;
}

export interface CellGravityMove {
  type: PieceType;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

export interface CellGravityAnimation {
  moves: CellGravityMove[];
  elapsed: number;
  duration: number;
}

export interface LandingAnimation {
  cells: PairCell[];
  elapsed: number;
  duration: number;
}

export interface MarkGrid {
  cells: boolean[][];
  count: number;
}

export interface EraseBurst {
  type: PieceType;
  count: number;
  x: number;
  y: number;
  angle: number;
  distance: number;
}

export interface ChainBurst {
  chain: number;
  x: number;
  y: number;
}

export interface ColorComboBurst {
  colorCount: number;
  x: number;
  y: number;
  angle: number;
}

export interface EraseAnimation {
  marked: boolean[][];
  frame: number;
  seed: number;
  erased: number;
  chain: number;
  bursts: EraseBurst[];
  chainBurst: ChainBurst | null;
  colorBurst: ColorComboBurst | null;
}

export interface EraseTextAnimation {
  bursts: EraseBurst[];
  chainBurst: ChainBurst | null;
  colorBurst: ColorComboBurst | null;
  elapsed: number;
  duration: number;
}

export interface GameOverAnimation {
  elapsed: number;
  duration: number;
}

export interface GameSettings {
  bgm: boolean;
  se: boolean;
  bgmVolume: number;
  seVolume: number;
  touchControls: boolean;
  language: LanguageSetting;
}

export interface Achievements {
  highScore: number;
  maxLevel: number;
  maxChain: number;
  maxSimultaneousErase: number;
}

export interface RuntimeSnapshot {
  board: Board;
  state: SceneState;
  score: number;
  level: number;
  levelProgress: number;
  erasedTotal: number;
  currentPair: FallingPair | null;
  nextPair: FallingPair | null;
  pairAnimation: PairAnimation | null;
  gravityAnimation: CellGravityAnimation | null;
  landingAnimation: LandingAnimation | null;
  eraseAnimation: EraseAnimation | null;
  eraseTextAnimations: EraseTextAnimation[];
  gameOverAnimation: GameOverAnimation | null;
  showAttractBoard: boolean;
  lastScoreAdd: number;
  chain: number;
  gameOverReason: string;
  settings: GameSettings;
  achievements: Achievements;
}

export function asPieceType(value: number): PieceType {
  if (value < 1 || value > MAX_TYPE_COUNT || !Number.isInteger(value)) {
    throw new Error(`Invalid piece type: ${value}`);
  }
  return value as PieceType;
}

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array<Cell>(BOARD_WIDTH).fill(0));
}

export function cloneBoard(board: Board): Board {
  return board.map((row) => row.slice());
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && x < BOARD_WIDTH && y >= 0 && y < BOARD_HEIGHT;
}

export function createMarkGrid(): boolean[][] {
  return Array.from({ length: BOARD_HEIGHT }, () => Array<boolean>(BOARD_WIDTH).fill(false));
}

export function pairCells(pair: FallingPair): [PairCell, PairCell] {
  const orientation = pair.orientation ?? 0;
  const offsets: Record<PairOrientation, [number, number]> = {
    0: [0, 1],
    1: [1, 0],
    2: [0, -1],
    3: [-1, 0]
  };
  const [dx, dy] = offsets[orientation];
  return [
    { x: pair.x, y: pair.y, type: pair.upperType },
    { x: pair.x + dx, y: pair.y + dy, type: pair.lowerType }
  ];
}

export function pairWithPosition(pair: FallingPair, x: number, y: number): FallingPair {
  return { ...pair, x, y };
}

export function defaultSettings(): GameSettings {
  return {
    bgm: true,
    se: true,
    bgmVolume: 70,
    seVolume: 70,
    touchControls: true,
    language: "auto"
  };
}

export function defaultAchievements(): Achievements {
  return {
    highScore: 0,
    maxLevel: 1,
    maxChain: 0,
    maxSimultaneousErase: 0
  };
}
